/* @HEADER
 * @version 1.1.178 | 2026-10-17
 * @purpose The shell's organisation scope in one hook: which organisation the switcher names (re-read on `workspace-switched`), whether the person belongs to any organisation (re-read on the membership refresh), whether the active project is foreign to the chosen one, and whether the personal organisation is current.
 * @sidecar useOrganisationScope.ts.header.md
 * @layer app | @hex _none_ | @ctx app-shell
 * @public false
 * @edit careful
 */

// SpecRefs: ZVX-DEV-7460, ZVX-DEV-5003, ZVX-DEV-5020, ZVX-DEV-6090, ZVX-DEV-6225, ZVX-DEV-3620

import { useEffect, useMemo, useState } from 'react';
import { isActiveProjectOutOfScope } from '../lib/activeProjectOutOfScope';
import { resolvePersonalWorkspaceId, isPersonalOrgCurrent } from '../lib/projectOrgScope';
import { resolveActiveWorkspaceId } from '../features/admin/resolveActiveWorkspaceId';
import { getActiveProject } from '../lib/projectStore';
// prettier-ignore
// @ts-expect-error — framework-free .mjs decisions, typed by their JSDoc
import { readActiveOrgId } from '../features/auth/activeOrgStorage.mjs';
import {
  readAccountIdentity,
  hasOrganisationMembership,
} from '../features/account/accountIdentity';
import { MEMBERSHIPS_REFRESHED_EVENT } from '../auth/refreshMembershipsFromServer';

export interface OrganisationScope {
  /** The active project belongs to another organisation than the chosen one. */
  outOfScope: boolean;
  hasOrganisation: boolean;
  personalOrganisation: boolean;
}

/**
 * Moved out of AppShell unchanged. `hasOrganisation` and `personalOrganisation`
 * are still computed on every render, as they were; the membership bump is
 * state of this hook, so it re-renders the shell exactly as before.
 */
export function useOrganisationScope(): OrganisationScope {
  // ZVX-DEV-5003 — is the project on screen foreign to the organization the
  // switcher names? Read reactively: WorkspaceSwitcher deliberately does not
  // reload the page, it writes storage and fires `workspace-switched`, so a
  // value captured once at mount would answer about the previous choice.
  const [activeWorkspaceId, setActiveWorkspaceId] = useState<string | null>(() => {
    try {
      return readActiveOrgId(localStorage) as string | null;
    } catch {
      return null;
    }
  });
  useEffect(() => {
    function onSwitch() {
      try {
        setActiveWorkspaceId(readActiveOrgId(localStorage) as string | null);
      } catch {
        setActiveWorkspaceId(null);
      }
    }
    window.addEventListener('workspace-switched', onSwitch);
    return () => window.removeEventListener('workspace-switched', onSwitch);
  }, []);

  // ZVX-DEV-5020 — the membership list is refreshed from the server at boot,
  // and an organization the user was added to while signed in arrives AFTER
  // this component has already rendered. `hasOrganisation` below is computed
  // during render from the stored snapshot, so without this the new
  // organization is on disk and invisible until the next reload: the exact
  // shape of the bug being fixed, one layer up.
  const [membershipsVersion, setMembershipsVersion] = useState(0);
  useEffect(() => {
    function onRefreshed() {
      setMembershipsVersion((n) => n + 1);
    }
    window.addEventListener(MEMBERSHIPS_REFRESHED_EVENT, onRefreshed);
    return () => window.removeEventListener(MEMBERSHIPS_REFRESHED_EVENT, onRefreshed);
  }, []);
  const outOfScope = useMemo(() => {
    const identity = readAccountIdentity();
    // ZVX-DEV-6090 -- the stored id is a CHOICE, and a choice is only valid
    // among the workspaces this person belongs to. Logout does not clear the
    // key (JwtAuthAdapter.clearAllAuthKeys), so the next account on the same
    // device inherits the previous one's organization here; read raw, that
    // id made a fresh FREE account's own personal project «foreign» and put
    // the ZVX-DEV-5483 placard where its board should be (e2e act I, scene 5,
    // 2026-09-18). App.tsx and ProjectSwitcher already resolve the same key
    // through this membership-aware resolver; this was the one raw read.
    const workspaceId = resolveActiveWorkspaceId(identity, activeWorkspaceId);
    return isActiveProjectOutOfScope({
      activeProject: getActiveProject(),
      workspaceId,
      personalWorkspaceId: resolvePersonalWorkspaceId(identity?.workspaces),
    });
  }, [activeWorkspaceId]);
  // ZVX-DEV-3620: membership is read from the SERVER's statement of it —
  // `workspaces[]`, which `loadUserWorkspaces` selects straight from
  // `workspace_members`, not from projects. The reading this replaces ("the
  // active project carries a companyId") was measured wrong on staging: seven
  // memberships exist in organisations that hold no projects, and each such
  // member would have been shown NO organisation board — which this product
  // uses to mean "this is not for you". An empty board is the honest answer.
  // membershipsVersion is a dependency on purpose: the value is read from
  // localStorage, which React cannot observe. The bump is what makes a
  // membership that arrived after mount reach the screen.
  void membershipsVersion;
  const hasOrganisation = hasOrganisationMembership(readAccountIdentity());
  // ZVX-DEV-6225 (C6): the same decision the summary's cards make
  // (resolveOrgProjectIdsStrict), so the nav's «Все личные задачи» and the
  // board's content cannot disagree about where the person stands.
  const personalOrganisation = isPersonalOrgCurrent({
    workspaces: readAccountIdentity()?.workspaces,
    activeProject: getActiveProject(),
  });
  return { outOfScope, hasOrganisation, personalOrganisation };
}
